import { ZfFormField, ZfInput, ZfSelectOption } from '@zafire/react';
import { uniqBy } from 'lodash';
import moment from 'moment';
import type { FunctionComponent, KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { ISessionFilter } from '../../interfaces/client';

interface IProps {
    filters: ISessionFilter;
    availableCountryCodes: string[];
    onFilterChange: (filter: ISessionFilter) => void;
}

export const SessionFilter: FunctionComponent<IProps> = ({ filters, availableCountryCodes, onFilterChange }) => {
    const { t } = useTranslation();

    const onFieldChange = (key: keyof ISessionFilter, value: string) => onFilterChange({ ...filters, [key]: value });

    const countries = uniqBy(
        availableCountryCodes.filter((c) => !!c),
        'value',
    );

    const handleEnterKeyDown = (event: KeyboardEvent<HTMLElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            onFilterChange(filters);
        }
    };

    const isToDateBeforeFromDate = moment(filters?.updatedAtToDate).isBefore(moment(filters?.updatedAtFromDate));

    return (
        <form onKeyDown={handleEnterKeyDown}>
            <div className="session-screen__filter">
                <ZfFormField label={t('sessionClientName')}>
                    <ZfInput
                        value={filters?.clientFullName}
                        onInputChange={(event) => onFieldChange('clientFullName', event.detail)}
                        placeholder={t('sessionTypeName')}
                    />
                </ZfFormField>
                <ZfFormField label={t('sessionRmName')}>
                    <ZfInput
                        value={filters?.rmFullName}
                        onInputChange={(event) => onFieldChange('rmFullName', event.detail)}
                        placeholder={t('sessionTypeName')}
                    />
                </ZfFormField>
                <ZfFormField label={t('sessionCountryOfResidence')}>
                    <ZfInput
                        type="select"
                        value={filters?.countryCode}
                        onSelectChange={(event) => onFieldChange('countryCode', event.detail as string)}
                        placeholder={t('sessionCountryOfResidencePlaceholder')}
                    >
                        {countries.map((country) => (
                            <ZfSelectOption key={country} value={country} description={country} />
                        ))}
                    </ZfInput>
                </ZfFormField>
                <div className="session-screen__filter__date-picker">
                    <div className="session-screen__filter__date-picker__from">
                        <ZfFormField label={`${t('sessionLastModifiedDate')} ${t('sessionFromDate')}`}>
                            <ZfInput
                                type="date"
                                value={filters?.updatedAtFromDate ?? undefined}
                                onInputChange={(e) => onFieldChange('updatedAtFromDate', e.detail)}
                            />
                        </ZfFormField>
                    </div>
                    <div className="session-screen__filter__date-picker__to">
                        <ZfFormField label={t('sessionToDate')} alerts={isToDateBeforeFromDate ? [t('toDateMustBeSameOrAfterFromDate')] : undefined}>
                            <ZfInput
                                type="date"
                                value={filters?.updatedAtToDate ?? undefined}
                                onInputChange={(e) => onFieldChange('updatedAtToDate', e.detail)}
                                error={isToDateBeforeFromDate}
                            />
                        </ZfFormField>
                    </div>
                </div>
            </div>
        </form>
    );
};
